import React, { useEffect, useState } from 'react';
import { Drawer } from '../../components/ui/Drawer';
import { BvnProviders, providerName } from '../../components/ui/BvnProviders';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Modal } from '../../components/ui/Modal';
import { usersApi } from '../../api/users';
import { PayIQUser } from '../../types';
import { useAuth } from '../../hooks/useAuth';

interface UserDetailProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const tierColors: Record<string, 'neutral' | 'info' | 'success' | 'premium'> = {
  tier_0: 'neutral',
  tier_1: 'info',
  tier_2: 'success',
  tier_3: 'premium',
};

const statusColors: Record<string, 'success' | 'danger' | 'neutral'> = {
  active: 'success',
  suspended: 'danger',
  deactivated: 'neutral',
};

const formatTier = (tier: string) =>
  tier.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateOnly = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid var(--neutral-200)',
      alignItems: 'center',
    }}
  >
    <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>{label}</span>
    <div style={{ fontSize: 13, color: 'var(--neutral-800)' }}>{children}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: 'var(--neutral-500)',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginTop: 24,
      marginBottom: 8,
    }}
  >
    {children}
  </h3>
);

const linkStyle: React.CSSProperties = {
  color: 'var(--blue-600)',
  fontSize: 13,
  fontWeight: 500,
};

type PendingAction =
  | { type: 'tier'; tier: string }
  | { type: 'suspend' }
  | { type: 'reactivate' }
  | { type: 'premium'; grant: boolean }
  | { type: 'reset_login' }
  | null;

export const UserDetail: React.FC<UserDetailProps> = ({ userId, isOpen, onClose, onUpdate }) => {
  const { admin } = useAuth();
  const [user, setUser] = useState<PayIQUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'profile'>('details');
  const [revealTarget, setRevealTarget] = useState<'bvn' | 'nin' | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealedIdentifier, setRevealedIdentifier] = useState<{
    type: 'bvn' | 'nin';
    value: string;
  } | null>(null);

  const canManage = admin && (admin.role === 'super_admin' || admin.role === 'operations');
  const canRevealIdentifiers = admin?.role === 'super_admin';

  useEffect(() => {
    if (!isOpen || !userId) {
      setUser(null);
      setActiveTab('details');
      setRevealedIdentifier(null);
      return;
    }
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await usersApi.getUserById(userId);
        setUser(data);
      } catch {
        setError('Failed to load user details.');
      } finally {
        setLoading(false);
      }
    };
    void fetchUser();
  }, [isOpen, userId]);

  useEffect(() => {
    if (!revealedIdentifier) return;
    const timeout = window.setTimeout(() => setRevealedIdentifier(null), 30_000);
    return () => window.clearTimeout(timeout);
  }, [revealedIdentifier]);

  const revealIdentifier = async () => {
    if (!user || !revealTarget || !currentPassword) return;
    try {
      setIsRevealing(true);
      const result = await usersApi.revealSensitiveIdentifier(
        user.id,
        revealTarget,
        currentPassword,
      );
      setRevealedIdentifier({ type: revealTarget, value: result.value });
      setCurrentPassword('');
      setRevealTarget(null);
    } catch {
      setError('Unable to reveal this identifier. Check your password and access level.');
    } finally {
      setIsRevealing(false);
    }
  };

  const executeAction = async (reason?: string) => {
    if (!user || !pendingAction) return;
    try {
      setActionLoading(true);
      let updated: PayIQUser | null = null;
      switch (pendingAction.type) {
        case 'tier':
          updated = await usersApi.updateTier(user.id, pendingAction.tier, reason);
          break;
        case 'suspend':
          updated = await usersApi.updateStatus(user.id, 'suspended', reason);
          break;
        case 'reactivate':
          updated = await usersApi.updateStatus(user.id, 'active', reason);
          break;
        case 'premium':
          updated = await usersApi.togglePremium(user.id, pendingAction.grant);
          break;
        case 'reset_login':
          await usersApi.resetLoginAttempts(user.id);
          break;
      }
      if (updated) setUser(updated);
      onUpdate();
      setPendingAction(null);
    } catch {
      setError('Action failed. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getDialogConfig = () => {
    if (!pendingAction || !user) return null;
    switch (pendingAction.type) {
      case 'tier':
        return {
          title: `Change tier to ${formatTier(pendingAction.tier)}?`,
          message: `This will move ${user.firstName ?? 'this user'} from ${formatTier(user.tier)} to ${formatTier(pendingAction.tier)}. The change is logged.`,
          confirmLabel: 'Change Tier',
          variant: 'primary' as const,
          requireReason: true,
        };
      case 'suspend':
        return {
          title: 'Suspend this user?',
          message: 'The user will be locked out and unable to access PayIQ. They can be reactivated at any time.',
          confirmLabel: 'Suspend User',
          variant: 'danger' as const,
          requireReason: true,
        };
      case 'reactivate':
        return {
          title: 'Reactivate this user?',
          message: 'The user will regain full access to their PayIQ account.',
          confirmLabel: 'Reactivate',
          variant: 'primary' as const,
          requireReason: false,
        };
      case 'premium':
        return {
          title: pendingAction.grant ? 'Grant Premium access?' : 'Remove Premium access?',
          message: pendingAction.grant
            ? 'This user will get access to all premium AI agents and features. No payment will be charged.'
            : 'This user will lose access to premium features immediately.',
          confirmLabel: pendingAction.grant ? 'Grant Premium' : 'Remove Premium',
          variant: pendingAction.grant ? ('primary' as const) : ('danger' as const),
          requireReason: false,
        };
      case 'reset_login':
        return {
          title: 'Reset failed login attempts?',
          message: 'This will clear all failed login attempts and unlock the account if locked.',
          confirmLabel: 'Reset',
          variant: 'primary' as const,
          requireReason: false,
        };
    }
  };

  const dialog = getDialogConfig();
  const title = user
    ? user.firstName || user.lastName
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      : 'Unnamed User'
    : 'Loading…';
  const verifiedSelfieUrl = user?.isFaceVerified ? user.selfieUrl : null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={user?.phoneNumber}
        width={600}
        actions={
          user && canManage ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setPendingAction({ type: 'reset_login' })}>
                Reset Login Attempts
              </Button>
              {user.status === 'active' ? (
                <Button variant="danger" size="sm" onClick={() => setPendingAction({ type: 'suspend' })}>
                  Suspend
                </Button>
              ) : (
                <Button size="sm" onClick={() => setPendingAction({ type: 'reactivate' })}>
                  Reactivate
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : error ? (
          <p style={{ color: 'var(--danger-600)', textAlign: 'center', padding: 24 }}>{error}</p>
        ) : user ? (
          <>
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                paddingBottom: 16,
                borderBottom: '1px solid var(--neutral-200)',
              }}
            >
              {verifiedSelfieUrl ? (
                <img
                  src={verifiedSelfieUrl}
                  alt="Verified selfie"
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid var(--neutral-200)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'var(--blue-100)',
                    color: 'var(--blue-700)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  {(user.firstName?.[0] ?? '?').toUpperCase()}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Badge variant={tierColors[user.tier]}>{formatTier(user.tier)}</Badge>
                  <Badge variant={statusColors[user.status]}>{user.status}</Badge>
                  {user.isPremium && <Badge variant="premium">Premium</Badge>}
                </div>
                <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  Joined {formatDateOnly(user.createdAt)}
                </span>
              </div>
            </div>

            <div
              role="tablist"
              aria-label="User details tabs"
              style={{ display: 'flex', gap: 20, borderBottom: '1px solid var(--neutral-200)', marginTop: 20 }}
            >
              {([
                ['details', 'Details'],
                ['profile', 'Profile Picture'],
              ] as const).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    border: 0,
                    borderBottom: activeTab === tab ? '2px solid var(--blue-600)' : '2px solid transparent',
                    background: 'transparent',
                    color: activeTab === tab ? 'var(--blue-700)' : 'var(--neutral-500)',
                    fontFamily: 'var(--font-family)',
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '0 2px 10px',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'profile' ? (
              <div style={{ padding: '28px 0', display: 'grid', justifyItems: 'center', gap: 14 }}>
                {user.profilePhoto ? (
                  <img
                    src={user.profilePhoto}
                    alt="Current user profile"
                    style={{ width: 220, height: 220, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--neutral-200)' }}
                  />
                ) : (
                  <div style={{ width: 220, height: 220, borderRadius: '50%', background: 'var(--blue-100)', color: 'var(--blue-700)', display: 'grid', placeItems: 'center', fontSize: 64, fontWeight: 700 }}>
                    {(user.firstName?.[0] ?? '?').toUpperCase()}
                  </div>
                )}
                <div style={{ textAlign: 'center', display: 'grid', gap: 4 }}>
                  <strong style={{ fontSize: 14, color: 'var(--neutral-800)' }}>Current profile picture</strong>
                  <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    {user.profilePhoto && user.profilePhoto === user.selfieUrl
                      ? 'Verified Tier-2 selfie'
                      : user.profilePhoto
                        ? 'User-selected image'
                        : 'No profile picture set'}
                  </span>
                </div>
              </div>
            ) : (
              <>
            <SectionTitle>Personal Information</SectionTitle>
            <InfoRow label="First Name">{user.firstName ?? '—'}</InfoRow>
            <InfoRow label="Last Name">{user.lastName ?? '—'}</InfoRow>
            <InfoRow label="Middle Name">{user.middleName ?? '—'}</InfoRow>
            <InfoRow label="Date of Birth">{formatDateOnly(user.dateOfBirth)}</InfoRow>
            <InfoRow label="Gender">
              {user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : '—'}
            </InfoRow>
            <InfoRow label="Primary Use">{user.primaryUse ?? '—'}</InfoRow>
            <InfoRow label="Interests">
              {user.interests && user.interests.length > 0 ? (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {user.interests.map((i) => (
                    <Badge key={i} variant="neutral">{i}</Badge>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </InfoRow>

            <SectionTitle>Contact</SectionTitle>
            <InfoRow label="Phone Number">{user.phoneNumber}</InfoRow>
            <InfoRow label="Email">{user.email ?? '—'}</InfoRow>
            <InfoRow label="Contact Changes">
              {user.contactChangeHistory?.length ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {user.contactChangeHistory.map((change) => (
                    <div key={change.id} style={{ display: 'grid', gap: 2 }}>
                      <span style={{ color: 'var(--neutral-800)' }}>
                        {change.field === 'email' ? 'Email' : 'Phone'}: {change.previousValueMasked} → {change.newValueMasked}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--neutral-500)' }}>{formatDate(change.changedAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                'No verified contact changes'
              )}
            </InfoRow>

            <SectionTitle>Account</SectionTitle>
            <InfoRow label="User ID">
              <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{user.id}</span>
            </InfoRow>
            <InfoRow label="Account Number">
              <span style={{ fontFamily: 'monospace' }}>{user.accountNumber ?? '—'}</span>
            </InfoRow>
            <InfoRow label="Last Login">{formatDate(user.lastLoginAt)}</InfoRow>
            <InfoRow label="Failed Logins">
              <span
                style={{
                  color: user.failedLoginAttempts > 0 ? 'var(--danger-600)' : 'var(--neutral-700)',
                  fontWeight: user.failedLoginAttempts > 0 ? 600 : 400,
                }}
              >
                {user.failedLoginAttempts}
              </span>
            </InfoRow>
            {user.lockedUntil && (
              <InfoRow label="Locked Until">
                <span style={{ color: 'var(--danger-600)' }}>{formatDate(user.lockedUntil)}</span>
              </InfoRow>
            )}

            <SectionTitle>KYC — Tier 1</SectionTitle>
            <InfoRow label="BVN">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace' }}>
                  {revealedIdentifier?.type === 'bvn' ? revealedIdentifier.value : user.bvnMasked ?? '—'}
                </span>
                {canRevealIdentifiers && user.bvnMasked && (
                  <Button variant="ghost" size="sm" onClick={() => setRevealTarget('bvn')}>
                    {revealedIdentifier?.type === 'bvn' ? 'Reveal again' : 'Reveal'}
                  </Button>
                )}
              </div>
            </InfoRow>
            {user.bvnProviders && (
              <InfoRow label="BVN checks">
                <BvnProviders
                  dojah={user.bvnProviders.dojahPassedAt ? 'verified' : 'missing'}
                  safehaven={user.bvnProviders.safehaven}
                  lookupProvider={user.kycProviders?.bvn}
                />
              </InfoRow>
            )}
            <InfoRow label="Phone Verified">
              <Badge variant={user.isPhoneVerified ? 'success' : 'neutral'}>
                {user.isPhoneVerified ? 'Verified' : 'Not verified'}
              </Badge>
            </InfoRow>
            <InfoRow label="Email Verified">
              <Badge variant={user.isEmailVerified ? 'success' : 'neutral'}>
                {user.isEmailVerified ? 'Verified' : 'Not verified'}
              </Badge>
            </InfoRow>

            <SectionTitle>KYC — Tier 2</SectionTitle>
            <InfoRow label="NIN">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace' }}>
                  {revealedIdentifier?.type === 'nin' ? revealedIdentifier.value : user.ninMasked ?? '—'}
                </span>
                {canRevealIdentifiers && user.ninMasked && (
                  <Button variant="ghost" size="sm" onClick={() => setRevealTarget('nin')}>
                    {revealedIdentifier?.type === 'nin' ? 'Reveal again' : 'Reveal'}
                  </Button>
                )}
                {providerName(user.kycProviders?.nin) ? (
                  <Badge variant="neutral">Checked with {providerName(user.kycProviders?.nin)}</Badge>
                ) : null}
              </div>
            </InfoRow>
            <InfoRow label="Face Verified">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Badge variant={user.isFaceVerified ? 'success' : 'neutral'}>
                  {user.isFaceVerified ? 'Verified' : 'Not verified'}
                </Badge>
                {providerName(user.kycProviders?.face) ? (
                  <Badge variant="neutral">Checked with {providerName(user.kycProviders?.face)}</Badge>
                ) : null}
              </div>
            </InfoRow>
            {user.selfieUrl && (
              <InfoRow label="Selfie">
                <a href={user.selfieUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  View selfie ↗
                </a>
              </InfoRow>
            )}

            <SectionTitle>KYC — Tier 3 (Address)</SectionTitle>
            <InfoRow label="Address Verified">
              <Badge variant={user.isAddressVerified ? 'success' : 'neutral'}>
                {user.isAddressVerified ? 'Verified' : 'Not verified'}
              </Badge>
            </InfoRow>
            <InfoRow label="Address">{user.address ?? '—'}</InfoRow>
            <InfoRow label="State">{user.state ?? '—'}</InfoRow>
            <InfoRow label="LGA">{user.lga ?? '—'}</InfoRow>
            <InfoRow label="Landmark">{user.landmark ?? '—'}</InfoRow>
            <InfoRow label="Document Type">{user.addressDocumentType ?? '—'}</InfoRow>
            {user.addressDocumentUrl && (
              <InfoRow label="Document">
                <a href={user.addressDocumentUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  View document ↗
                </a>
              </InfoRow>
            )}

            {canManage && (
              <>
                <SectionTitle>Tier Management</SectionTitle>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 0' }}>
                  {['tier_0', 'tier_1', 'tier_2', 'tier_3'].map((t) => (
                    <button
                      key={t}
                      onClick={() => t !== user.tier && setPendingAction({ type: 'tier', tier: t })}
                      disabled={t === user.tier}
                      style={{
                        padding: '6px 14px',
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: 'var(--font-family)',
                        borderRadius: 'var(--radius-md)',
                        background: t === user.tier ? 'var(--blue-600)' : 'var(--neutral-200)',
                        color: t === user.tier ? 'var(--neutral-100)' : 'var(--neutral-700)',
                        border: '1px solid',
                        borderColor: t === user.tier ? 'var(--blue-600)' : 'var(--neutral-300)',
                        cursor: t === user.tier ? 'not-allowed' : 'pointer',
                        transition: 'all 150ms ease',
                      }}
                    >
                      {formatTier(t)}
                    </button>
                  ))}
                </div>

                <SectionTitle>Premium Access</SectionTitle>
                <div style={{ padding: '8px 0' }}>
                  <Button
                    variant={user.isPremium ? 'danger' : 'primary'}
                    size="sm"
                    onClick={() => setPendingAction({ type: 'premium', grant: !user.isPremium })}
                  >
                    {user.isPremium ? 'Remove Premium' : 'Grant Premium'}
                  </Button>
                </div>
              </>
            )}
              </>
            )}
          </>
        ) : null}
      </Drawer>

      {dialog && (
        <ConfirmDialog
          isOpen={pendingAction !== null}
          title={dialog.title}
          message={dialog.message}
          confirmLabel={dialog.confirmLabel}
          variant={dialog.variant}
          requireReason={dialog.requireReason}
          loading={actionLoading}
          onConfirm={executeAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      <Modal
        isOpen={revealTarget !== null}
        onClose={() => {
          if (!isRevealing) {
            setRevealTarget(null);
            setCurrentPassword('');
          }
        }}
        title={`Reveal ${revealTarget?.toUpperCase() ?? 'identifier'}`}
      >
        <div style={{ display: 'grid', gap: 16 }}>
          <p style={{ margin: 0, color: 'var(--neutral-600)', fontSize: 13, lineHeight: 1.5 }}>
            Confirm your password to reveal this value. This access is logged and the value will hide after 30 seconds.
          </p>
          <input
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            autoFocus
            disabled={isRevealing}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void revealIdentifier();
            }}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-family)',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="ghost" onClick={() => setRevealTarget(null)} disabled={isRevealing}>Cancel</Button>
            <Button onClick={() => void revealIdentifier()} disabled={!currentPassword || isRevealing}>
              {isRevealing ? 'Revealing...' : 'Reveal'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
